import React, { useEffect, useRef, useState } from 'react';
import { CARD } from '../ui';
import { GRID } from '../dashboard/theme';
import { formatMoney, formatMoneyCompact } from './format';
import { COLLECTED_COLOR, SURFACE, niceMoneyCeil } from './theme';

const PLOT_HEIGHT = 200;
const SEGMENT_GAP = 2;
const LABEL_BUDGET = 56; // px of axis per x label, so narrow cards thin them out

// Measured rather than assumed: the collected line is drawn in pixels over a
// plot whose width depends on the card it sits in. Re-measures when the table
// view hands the plot back.
const useWidth = (remountKey) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => setWidth(node.getBoundingClientRect().width);
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(node);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [remountKey]);
  return [ref, width];
};

const tickIndexes = (length, maxTicks) => {
  if (length <= maxTicks) return [...Array(length).keys()];
  const step = (length - 1) / (maxTicks - 1);
  return [...new Set([...Array(maxTicks).keys()].map((i) => Math.round(i * step)))];
};

const LineKey = ({ color }) => (
  <span className="inline-block w-3 h-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
);

const Swatch = ({ color }) => (
  <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />
);

// One column per period, stacked by series, with cash collected drawn over the
// top as a line on the same dollar axis. Columns are buttons: hovering or
// focusing one shows every series for it, and clicking selects it when the
// chart has ``onSelect``. A column flagged ``estimate`` is a running figure for
// a period that hasn't been invoiced yet, and is drawn lighter.
const MonthlyChart = ({
  title,
  subtitle,
  actions,
  columns,
  series,
  showCollected = false,
  selectedKey = null,
  onSelect,
  selectLabel = 'Select',
  footnote,
  emptyLabel = 'Nothing billed in this range.',
}) => {
  const [active, setActive] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const [collectedOn, setCollectedOn] = useState(true);
  const [plotRef, width] = useWidth(asTable);

  const drawCollected = showCollected && collectedOn;
  const peak = Math.max(0, ...columns.map((c) => c.total), ...(drawCollected ? columns.map((c) => c.collected || 0) : []));
  const axisMax = niceMoneyCeil(peak);
  const maxTicks = width ? Math.max(2, Math.min(columns.length, Math.floor(width / LABEL_BUDGET))) : 6;
  const ticks = tickIndexes(columns.length, maxTicks);
  const y = (cents) => PLOT_HEIGHT - (cents / axisMax) * PLOT_HEIGHT;
  const x = (index) => ((index + 0.5) / columns.length) * width;
  const hovered = active !== null ? columns[active] : null;
  const tooltipLeft = active === null ? 0 : Math.min(90, Math.max(10, ((active + 0.5) / columns.length) * 100));
  const showLegend = series.length > 1 || showCollected;
  const empty = columns.every((c) => c.total === 0 && !(c.collected > 0));

  return (
    <div className={`${CARD} p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          <button
            type="button"
            onClick={() => setAsTable((v) => !v)}
            className="bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 border border-gray-600 hover:border-gray-500"
          >
            {asTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </div>

      {showLegend && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5 text-xs text-gray-400">
          {series.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5">
              <Swatch color={s.color} />
              {s.label}
            </span>
          ))}
          {showCollected && (
            <button
              type="button"
              onClick={() => setCollectedOn((v) => !v)}
              aria-pressed={collectedOn}
              className={`inline-flex items-center gap-1.5 bg-transparent hover:bg-gray-700/40 px-1.5 py-0.5 -mx-1.5 rounded text-xs transition-colors ${
                collectedOn ? 'text-gray-300' : 'text-gray-500 line-through'
              }`}
            >
              <LineKey color={COLLECTED_COLOR} />
              Collected
            </button>
          )}
        </div>
      )}

      {asTable ? (
        <div className="max-h-80 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-800">
              <tr className="text-left text-gray-400">
                <th scope="col" className="py-2 pr-3 font-medium">Period</th>
                {series.length > 1 && series.map((s) => (
                  <th key={s.id} scope="col" className="py-2 px-3 font-medium text-right whitespace-nowrap">{s.label}</th>
                ))}
                <th scope="col" className="py-2 px-3 font-medium text-right">Billed</th>
                {showCollected && <th scope="col" className="py-2 pl-3 font-medium text-right">Collected</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/60">
              {columns.map((column) => (
                <tr key={column.key}>
                  <td className="py-2 pr-3 text-gray-300 whitespace-nowrap">
                    {column.fullLabel}
                    {column.estimate && <span className="text-gray-500"> (so far)</span>}
                  </td>
                  {series.length > 1 && series.map((s) => (
                    <td key={s.id} className="py-2 px-3 text-right text-gray-300 tabular-nums">
                      {formatMoney(column.segments[s.id] || 0)}
                    </td>
                  ))}
                  <td className="py-2 px-3 text-right text-white tabular-nums">{formatMoney(column.total)}</td>
                  {showCollected && (
                    <td className="py-2 pl-3 text-right text-gray-300 tabular-nums">{formatMoney(column.collected || 0)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : empty ? (
        <p className="py-16 text-center text-sm text-gray-500">{emptyLabel}</p>
      ) : (
        <div className="flex">
          <div className="relative w-14 flex-shrink-0" style={{ height: PLOT_HEIGHT }}>
            {[axisMax, axisMax / 2, 0].map((tick, i) => (
              <span
                key={i}
                className="absolute right-2 text-xs text-gray-500 tabular-nums -translate-y-1/2 whitespace-nowrap"
                style={{ top: `${i * 50}%` }}
              >
                {formatMoneyCompact(tick, axisMax >= 1000000)}
              </span>
            ))}
          </div>

          <div className="flex-1 min-w-0">
            <div className="relative" style={{ height: PLOT_HEIGHT }} ref={plotRef}>
              {[0, 50, 100].map((top) => (
                <div key={top} className="absolute left-0 right-0" style={{ top: `${top}%`, height: 1, backgroundColor: GRID }} />
              ))}

              <div className="absolute inset-0 flex items-end">
                {columns.map((column, index) => {
                  const isSelected = selectedKey === column.key;
                  const dimmed = (selectedKey && !isSelected) || (active !== null && active !== index);
                  // Topmost first, so the rounded cap lands on the last series.
                  const stack = [...series].reverse().filter((s) => (column.segments[s.id] || 0) / axisMax * PLOT_HEIGHT >= 0.5);
                  return (
                    <button
                      key={column.key}
                      type="button"
                      onMouseEnter={() => setActive(index)}
                      onMouseLeave={() => setActive((cur) => (cur === index ? null : cur))}
                      onFocus={() => setActive(index)}
                      onBlur={() => setActive((cur) => (cur === index ? null : cur))}
                      onClick={onSelect ? () => onSelect(column.key) : undefined}
                      aria-pressed={onSelect ? isSelected : undefined}
                      aria-label={`${selectLabel} ${column.fullLabel}: ${formatMoney(column.total)} billed${
                        showCollected ? `, ${formatMoney(column.collected || 0)} collected` : ''
                      }`}
                      // hover:bg-* is required: App.css's global button:hover
                      // color outranks a plain bg-* class.
                      className={`flex-1 min-w-0 h-full flex flex-col justify-end items-center p-0 rounded-t transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 ${
                        isSelected ? 'bg-purple-500/15 hover:bg-purple-500/20' : 'bg-transparent hover:bg-purple-500/10'
                      } ${onSelect ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      {stack.map((s, i) => {
                        const raw = ((column.segments[s.id] || 0) / axisMax) * PLOT_HEIGHT;
                        const bottom = i === stack.length - 1;
                        return (
                          <span
                            key={s.id}
                            className="block transition-opacity duration-150"
                            style={{
                              // Never the whole slot: on a narrow card the
                              // leftover is what keeps neighbors apart.
                              width: '72%',
                              maxWidth: 24,
                              height: Math.max(1, bottom ? raw : raw - SEGMENT_GAP),
                              marginBottom: bottom ? 0 : SEGMENT_GAP,
                              backgroundColor: s.color,
                              borderRadius: i === 0 ? '4px 4px 0 0' : 0,
                              opacity: (dimmed ? 0.35 : 1) * (column.estimate ? 0.45 : 1),
                            }}
                          />
                        );
                      })}
                    </button>
                  );
                })}
              </div>

              {drawCollected && width > 0 && (
                <svg
                  className="absolute inset-0 pointer-events-none overflow-visible"
                  width={width}
                  height={PLOT_HEIGHT}
                  aria-hidden="true"
                >
                  {/* A surface-colored halo under the line keeps it legible
                      where it crosses a fill as light as itself. */}
                  {[{ stroke: SURFACE, width: 6 }, { stroke: COLLECTED_COLOR, width: 2 }].map((pass) => (
                    <polyline
                      key={pass.width}
                      points={columns.map((c, i) => `${x(i)},${y(c.collected || 0)}`).join(' ')}
                      fill="none"
                      stroke={pass.stroke}
                      strokeWidth={pass.width}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                  ))}
                  {columns.map((c, i) => (
                    <circle key={c.key} cx={x(i)} cy={y(c.collected || 0)} r="4" fill={COLLECTED_COLOR} stroke={SURFACE} strokeWidth="2" />
                  ))}
                </svg>
              )}

              {hovered && (
                <div
                  role="status"
                  className="absolute -top-2 z-10 -translate-x-1/2 -translate-y-full pointer-events-none rounded-lg border border-purple-500/30 bg-gray-900 px-3 py-2 shadow-xl min-w-[11rem]"
                  style={{ left: `${tooltipLeft}%` }}
                >
                  <p className="text-xs text-gray-400 mb-1 whitespace-nowrap">
                    {hovered.fullLabel}
                    {hovered.estimate && ' · so far, not invoiced'}
                  </p>
                  <p className="text-base font-semibold text-white whitespace-nowrap">
                    {formatMoney(hovered.total)} <span className="text-sm font-normal text-gray-400">billed</span>
                  </p>
                  {series.length > 1 && (
                    <ul className="mt-1 space-y-0.5">
                      {series.map((s) => (
                        <li key={s.id} className="flex items-center gap-2 text-xs whitespace-nowrap">
                          <LineKey color={s.color} />
                          <span className="text-white tabular-nums">{formatMoney(hovered.segments[s.id] || 0)}</span>
                          <span className="text-gray-400">{s.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {showCollected && (
                    <p className="mt-1 pt-1 border-t border-gray-700 flex items-center gap-2 text-xs whitespace-nowrap">
                      <LineKey color={COLLECTED_COLOR} />
                      <span className="text-white tabular-nums">{formatMoney(hovered.collected || 0)}</span>
                      <span className="text-gray-400">collected</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="relative h-5 mt-2">
              {ticks.map((index) => (
                <span
                  key={columns[index].key}
                  className="absolute top-0 -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap"
                  style={{ left: `${((index + 0.5) / columns.length) * 100}%` }}
                >
                  {columns[index].axisLabel}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {footnote && <p className="mt-4 text-xs text-gray-500">{footnote}</p>}
    </div>
  );
};

export default MonthlyChart;
